import { useRef, useState } from 'react'
import { components } from 'react-select'
import AsyncSelect from 'react-select/async'
import type { ControlProps, SelectInstance } from 'react-select'
import Search from '@mui/icons-material/Search'
import { useNavigate } from 'react-router'
import debounce from 'debounce-promise'
import Fuse from 'fuse.js'
import { userPeopleSearch } from '@fireflyhealth/api'
import PatientSearchOption from './PatientSearchOption'
import {
  useRecentlyViewedPeople,
  type PersonDemographics,
  type PersonSearchOption,
} from './PeopleSearchUtils'
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  Popover,
  Typography,
} from '@mui/material'
import IconSettings from '@mui/icons-material/Settings'
import { useLocalStorage } from '~/shared/hooks/useLocalStorage'
import { useFocusSearchHotkey } from '~/shared/hooks/useFocusSearchHotkey'
import { personRowName } from '~/entities/person'

const Control = (props: ControlProps<PersonSearchOption, false>) => {
  return (
    <components.Control {...props}>
      <Search style={{ marginLeft: 8, color: '#7b8f9a' }} />
      {props.children}
    </components.Control>
  )
}

const fuseOptions = {
  isCaseSensitive: false,
  distance: 100,
  findAllMatches: false,
  includeMatches: false,
  includeScore: false,
  location: 0,
  minMatchCharLength: 0,
  shouldSort: true,
  threshold: 0.6,
  useExtendedSearch: false,
  keys: ['phone_number', 'label', 'id'],
}

// Transform people data to the format expected by the select component.
// `label` is one of the Fuse keys above, so a person whose label is blank is dropped from the
// menu no matter what the server matched on.
const makeSelectOptions = (people: PersonDemographics[]): PersonSearchOption[] =>
  people.map(person => ({
    ...person,
    value: person.id,
    label: personRowName(person),
    id: person.user_id ?? person.id,
  }))

const MAX_RESULTS = 50
const SEARCH_DEBOUNCE_TIME = 300

const debouncedSearch = debounce(
  async (searchTermInput: string, includeOnboardingInitialized: boolean) => {
    const response = await userPeopleSearch.list({
      params: {
        search: searchTermInput,
        limit: MAX_RESULTS,
        include_onboarding_initialized: includeOnboardingInitialized,
      },
    })
    const options = makeSelectOptions(response.data.results)

    const fuse = new Fuse(options, fuseOptions)
    // `.item` matters and nothing here would catch its absence: fuse 7 wraps hits as
    // `{ item, refIndex, score }`, and `debounce-promise` erases the return type on the
    // way to react-select, so the wrappers would reach the menu as blank options.
    return fuse.search(searchTermInput).map(result => result.item)
  },
  SEARCH_DEBOUNCE_TIME,
  { leading: false },
)

const PeopleSearch = () => {
  const navigate = useNavigate()
  const people = useRecentlyViewedPeople()
  const selectRef = useRef<SelectInstance<PersonSearchOption, false> | null>(null)
  const [includeOnboardingInitialized, setIncludeOnboardingInitialized] = useLocalStorage(
    'includeOnboardingInitialized',
    'yes',
  )
  const [showOptions, setShowOptions] = useState(false)
  const [optionsMenuAnchorEl, setOptionsMenuAnchorEl] = useState<HTMLElement | null>(null)

  useFocusSearchHotkey(event => {
    event.preventDefault()
    selectRef.current?.focus()
  })

  const loadOptions = (input: string) => {
    setShowOptions(false)
    return debouncedSearch(input, includeOnboardingInitialized === 'yes')
  }

  const toggleShowOptions = () => setShowOptions(prev => !prev)

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ display: 'inline-flex' }}>
          <AsyncSelect<PersonSearchOption>
            value={null}
            onChange={person => {
              if (person !== null) {
                if (person.user_id) void navigate(`/patients/${person.user_id}`)
                else void navigate(`/people/${person.id}`)
              }
            }}
            placeholder="Search patients"
            components={{ Control, Option: PatientSearchOption }}
            styles={{
              control: (provided, state) => ({
                ...provided,
                display: 'inline-flex',
                width: '32rem',
                height: 38,
                borderRadius: 5,
                backgroundColor: '#f3f3f3',
                border: 'none',
                padding: '0 6px',
              }),
              indicatorSeparator: () => ({ display: 'none' }),
              dropdownIndicator: () => ({ display: 'none' }),
            }}
            defaultOptions={[
              {
                label: 'Recently Viewed',
                options: makeSelectOptions(people),
              },
            ]}
            loadOptions={loadOptions}
            openMenuOnClick
            openMenuOnFocus
            tabSelectsValue={false}
            ref={selectRef}
          />
        </Box>
        <IconButton
          onClick={event => {
            toggleShowOptions()
            setOptionsMenuAnchorEl(event.currentTarget)
          }}
        >
          <IconSettings />
        </IconButton>
        <Popover
          anchorEl={optionsMenuAnchorEl}
          open={showOptions}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          onClose={() => setShowOptions(false)}
        >
          <Card>
            <CardContent>
              <Typography variant="caption">Search Options</Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeOnboardingInitialized === 'yes'}
                      onChange={() =>
                        setIncludeOnboardingInitialized(
                          includeOnboardingInitialized === 'yes' ? 'no' : 'yes',
                        )
                      }
                    />
                  }
                  label="Include patients in sign-up?"
                />
              </FormGroup>
            </CardContent>
          </Card>
        </Popover>
      </Box>
    </>
  )
}
export default PeopleSearch
